import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography, 
  Autocomplete, 
  FormHelperText, 
  Alert, 
  Stack,
  CircularProgress
} from '@mui/material';

import { 
  createAppointment, 
  getPatients, 
  getAppointmentTypes, 
  getPractitioners 
} from '../../api/calendarService';

interface AppointmentFormProps {
  open: boolean;
  onClose: () => void;
  selectedDate?: Date | null;
  practitionerId?: string;
  onAppointmentCreated: () => void;
}

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  cliniko_id?: string;
}

interface AppointmentType {
  id: string;
  name: string;
  duration: number;
  color?: string;
}

interface Practitioner {
  id: string;
  first_name: string;
  last_name: string;
  title?: string;
  name?: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ 
  open, 
  onClose, 
  selectedDate, 
  practitionerId, 
  onAppointmentCreated 
}) => {
  // Form state
  const [date, setDate] = useState<Date | null>(selectedDate || new Date());
  // Initialize times without modifying the date object (which was causing issues)
  const [startTime, setStartTime] = useState<Date | null>(() => {
    if (!date) return null;
    const newTime = new Date();
    newTime.setHours(9, 0, 0, 0);
    return newTime;
  });
  const [endTime, setEndTime] = useState<Date | null>(() => {
    if (!date) return null;
    const newTime = new Date();
    newTime.setHours(9, 30, 0, 0);
    return newTime;
  });
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);
  const [notes, setNotes] = useState<string>('');
  
  // API data
  const [patients, setPatients] = useState<Patient[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [patientsLoading, setPatientsLoading] = useState<boolean>(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);

  // Fetch appointment types and practitioners on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching practitioners and appointment types...');
        const practitionersPromise = getPractitioners();
        const appointmentTypesPromise = getAppointmentTypes();
        
        // Process practitioners
        try {
          const practitionersData = await practitionersPromise;
          console.log('Practitioners response:', practitionersData);
          const practitioners = practitionersData.practitioners || [];
          setPractitioners(practitioners);
          
          // Auto-select the practitioner if provided in props
          if (practitionerId && practitioners.length > 0) {
            const practitioner = practitioners.find(p => p.id === practitionerId);
            if (practitioner) {
              setSelectedPractitioner(practitioner);
              console.log('Auto-selected practitioner from props:', practitioner);
            }
          } 
          // Otherwise, select the first practitioner if available
          else if (practitioners.length > 0) {
            setSelectedPractitioner(practitioners[0]);
            console.log('Auto-selected first practitioner:', practitioners[0]);
          }
        } catch (practErr) {
          console.error('Error fetching practitioners:', practErr);
        }
        
        // Process appointment types
        try {
          const appointmentTypesData = await appointmentTypesPromise;
          console.log('Appointment types response:', appointmentTypesData);
          const appointmentTypes = appointmentTypesData.appointment_types || [];
          setAppointmentTypes(appointmentTypes);
        } catch (typeErr) {
          console.error('Error fetching appointment types:', typeErr);
        }
      } catch (err) {
        console.error('Error fetching form data:', err);
        setError('Failed to load form data. Please try again.');
      }
    };
    
    fetchData();
  }, [practitionerId]);
  
  // Update end time when appointment type changes
  useEffect(() => {
    if (selectedAppointmentType && startTime) {
      const duration = selectedAppointmentType.duration || 30; // Default to 30 minutes
      const newEndTime = new Date(startTime);
      newEndTime.setMinutes(startTime.getMinutes() + duration);
      setEndTime(newEndTime);
    }
  }, [selectedAppointmentType, startTime]);
  
  // Fetch patients with search
  useEffect(() => {
    const fetchPatients = async () => {
      if (patientSearchQuery.length < 2) return; // Don't search with less than 2 characters
      
      setPatientsLoading(true);
      try {
        console.log('Searching patients with query:', patientSearchQuery);
        const result = await getPatients(patientSearchQuery);
        console.log('Patient search response:', result);
        
        // Process patients data
        const patients = result.patients || [];
        console.log('Processed patients:', patients);
        
        if (patients.length === 0) {
          console.log('No patients found matching:', patientSearchQuery);
        } else {
          // Check if patients have required fields
          patients.forEach((patient, index) => {
            if (!patient.first_name || !patient.last_name) {
              console.warn(`Patient at index ${index} missing name:`, patient);
            }
            if (!patient.cliniko_id) {
              console.warn(`Patient at index ${index} missing cliniko_id:`, patient);
            }
          });
        }
        
        setPatients(patients);
      } catch (err) {
        console.error('Error searching patients:', err);
        setPatients([]); // Clear patients on error
      } finally {
        setPatientsLoading(false);
      }
    };
    
    const timer = setTimeout(() => {
      fetchPatients();
    }, 500); // Debounce search
    
    return () => clearTimeout(timer);
  }, [patientSearchQuery]);
  
  // Submit form
  const handleSubmit = async () => {
    setSubmitted(true);
    
    // Validate form
    if (!date || !startTime || !endTime || !selectedPatient || 
        !selectedAppointmentType || !selectedPractitioner) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Make sure the patient has a Cliniko ID
      if (!selectedPatient.cliniko_id) {
        setError('This patient does not have a Cliniko ID. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Combine date and time
      // Create a new date to avoid modifying the original
      const startDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        0,
        0
      );
      
      const endDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        endTime.getHours(),
        endTime.getMinutes(),
        0,
        0
      );
      
      console.log('Date selected:', date.toString());
      console.log('Start time selected:', startTime.toString());
      console.log('Combined start date/time:', startDate.toString());
      
      // Log what we're sending to the API
      console.log('Creating appointment with:', {
        practitionerId: selectedPractitioner.id,
        patientId: selectedPatient.cliniko_id,
        appointmentTypeId: selectedAppointmentType.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      await createAppointment({
        practitionerId: selectedPractitioner.id,
        patientId: selectedPatient.cliniko_id, // Always use Cliniko ID
        appointmentTypeId: selectedAppointmentType.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notes: notes
      });
      
      onAppointmentCreated();
      onClose();
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.error || 'Failed to create appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset form on close
  const handleClose = () => {
    setSubmitted(false);
    setError(null);
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        Create New Appointment
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={3}>
          {/* Date & Time */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Date"
                  type="date"
                  // Format the date directly in local time to avoid timezone issues
                  value={date ? 
                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` 
                    : ''
                  }
                  onChange={(e) => {
                    if (e.target.value) {
                      // Create date from input value - force local interpretation
                      const parts = e.target.value.split('-');
                      const year = parseInt(parts[0], 10);
                      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS
                      const day = parseInt(parts[2], 10);
                      
                      // Use local date constructor to prevent timezone shift
                      const newDate = new Date(year, month, day, 12, 0, 0); // Use noon to avoid DST issues
                      console.log('Selected date:', e.target.value, 'Created date object:', newDate.toString());
                      setDate(newDate);
                    } else {
                      setDate(null);
                    }
                  }}
                  required
                  error={submitted && !date}
                  helperText={submitted && !date ? 'Date is required' : ''}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                
                <TextField
                  label="Start Time"
                  type="time"
                  value={startTime ? 
                    `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}` 
                    : ''}
                  onChange={(e) => {
                    if (!date) return;
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newTime = new Date(date);
                    newTime.setHours(hours, minutes, 0, 0);
                    setStartTime(newTime);
                    
                    // Update end time if appointment type selected
                    if (selectedAppointmentType) {
                      const duration = selectedAppointmentType.duration || 30;
                      const newEndTime = new Date(newTime);
                      newEndTime.setMinutes(newTime.getMinutes() + duration);
                      setEndTime(newEndTime);
                    }
                  }}
                  required
                  error={submitted && !startTime}
                  helperText={submitted && !startTime ? 'Start time is required' : ''}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                
                <TextField
                  label="End Time"
                  type="time"
                  value={endTime ? 
                    `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}` 
                    : ''}
                  onChange={(e) => {
                    if (!date) return;
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newTime = new Date(date);
                    newTime.setHours(hours, minutes, 0, 0);
                    setEndTime(newTime);
                  }}
                  required
                  error={submitted && !endTime}
                  helperText={submitted && !endTime ? 'End time is required' : ''}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
              </Box>
          </Box>
          
          {/* Practitioner Selection */}
          <FormControl fullWidth error={submitted && !selectedPractitioner} required sx={{ mb: 2 }}>
            <InputLabel>Practitioner</InputLabel>
            <Select
              value={selectedPractitioner?.id || ''}
              label="Practitioner"
              onChange={(e) => {
                const practitionerId = e.target.value;
                const practitioner = practitioners.find(p => p.id === practitionerId);
                setSelectedPractitioner(practitioner || null);
              }}
            >
              {practitioners.map((practitioner) => (
                <MenuItem 
                  key={practitioner.id} 
                  value={practitioner.id}
                >
                  {practitioner.name || 
                    `${practitioner.title || ''} ${practitioner.first_name} ${practitioner.last_name}`.trim()}
                </MenuItem>
              ))}
            </Select>
            {submitted && !selectedPractitioner && (
              <FormHelperText>Practitioner is required</FormHelperText>
            )}
          </FormControl>
          
          {/* Patient Select */}
          <Autocomplete
            options={patients}
            getOptionLabel={(option) => {
              if (option.first_name && option.last_name) {
                return `${option.first_name} ${option.last_name}${option.email ? ` (${option.email})` : ''}`;
              }
              return option.id || 'Unknown Patient';
            }}
            value={selectedPatient}
            onChange={(_event, newValue) => {
              console.log('Selected patient:', newValue);
              setSelectedPatient(newValue);
            }}
            onInputChange={(_event, newInputValue) => {
              console.log('Search input changed to:', newInputValue);
              setPatientSearchQuery(newInputValue);
            }}
            loading={patientsLoading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, state) => {
              // If there's no search query, or it's less than 2 chars, show no options
              if (!patientSearchQuery || patientSearchQuery.length < 2) {
                return [];
              }
              
              // Apply client-side filtering as well for better UX
              const inputValue = state.inputValue.toLowerCase().trim();
              
              if (inputValue === '') {
                return options;
              }
              
              return options.filter(option => {
                const fullName = `${option.first_name} ${option.last_name}`.toLowerCase();
                const email = option.email ? option.email.toLowerCase() : '';
                
                return fullName.includes(inputValue) || email.includes(inputValue);
              });
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1">{option.first_name} {option.last_name}</Typography>
                  {option.email && <Typography variant="caption" color="text.secondary">{option.email}</Typography>}
                  {!option.cliniko_id && (
                    <Typography variant="caption" color="error">Missing Cliniko ID</Typography>
                  )}
                </Box>
              </li>
            )}
            noOptionsText={patientSearchQuery.length < 2 
              ? "Type at least 2 characters to search" 
              : "No patients found - check your search or add a new patient"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Patient"
                required
                error={submitted && !selectedPatient}
                helperText={submitted && !selectedPatient ? 'Patient is required' : 'Type to search patients'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {patientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          
          {/* Appointment Type Select */}
          <FormControl fullWidth error={submitted && !selectedAppointmentType} required>
            <InputLabel>Appointment Type</InputLabel>
            <Select
              value={selectedAppointmentType?.id || ''}
              label="Appointment Type"
              onChange={(e) => {
                const appointmentType = appointmentTypes.find(type => type.id === e.target.value);
                setSelectedAppointmentType(appointmentType || null);
              }}
            >
              {appointmentTypes.map((type) => (
                <MenuItem 
                  key={type.id} 
                  value={type.id}
                  sx={{ 
                    borderLeft: type.color ? `4px solid ${type.color}` : 'none',
                    paddingLeft: type.color ? 1 : 2
                  }}
                >
                  {type.name} ({type.duration} min)
                </MenuItem>
              ))}
            </Select>
            {submitted && !selectedAppointmentType && (
              <FormHelperText>Appointment type is required</FormHelperText>
            )}
          </FormControl>
          
          {/* Notes */}
          <TextField
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Create Appointment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentForm;